import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Card, Checkbox, ConfigProvider, Flex, Form, Input, Result, Typography, theme } from 'antd'
import nlNL from 'antd/locale/nl_NL'
import enUS from 'antd/locale/en_US'
import { ApplicationInput, Configuration, PreviewAppearance, Receipt, PRIVACY_URL } from '../public-form'
import { assetUrl } from '../api'
import { useTranslation } from 'react-i18next'
import '../public-form.css'
import { MobileNumberInput } from './mobile-number-input'
import { TurnstileCheck } from '../../components/turnstile-check'
import { parseMobileNumber } from '../phone-number'
import { publicFormTheme } from '../theme'

const EVAA_NAME = 'Evaa'

type PublicFormPageProps = {
    configuration: Configuration
    turnstileSiteKey?: string | null
    resolvedAppearance?: PreviewAppearance
    available?: boolean
    previewAppearance?: PreviewAppearance
    preview?: boolean
    onSubmit?: (input: Omit<ApplicationInput, 'idempotencyKey' | 'formVersion'>) => Promise<void>
    pending?: boolean
    receipt?: Receipt
    error?: string
    onReload?: () => void
}
export function PublicFormPage(props: PublicFormPageProps) {
    const config = props.configuration
    const [systemDark, setSystemDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches)
    useEffect(() => {
        const preference = window.matchMedia('(prefers-color-scheme: dark)')
        const update = () => setSystemDark(preference.matches)
        update()
        preference.addEventListener('change', update)
        return () => preference.removeEventListener('change', update)
    }, [])
    const appearance =
        props.previewAppearance ??
        (config.appearance === 'system' ? (systemDark ? 'dark' : 'light') : config.appearance)
    const activeTheme = useMemo(
        () => (appearance === 'dark' ? { ...config.theme, ...config.darkTheme } : config.theme),
        [appearance, config.theme, config.darkTheme]
    )
    const themeConfig = useMemo(() => publicFormTheme(activeTheme, appearance), [appearance, activeTheme])
    return (
        <ConfigProvider
            prefixCls="application"
            locale={config.locale === 'nl_NL' ? nlNL : enUS}
            theme={themeConfig}
        >
            <PublicFormContent
                {...props}
                resolvedAppearance={appearance}
            />
        </ConfigProvider>
    )
}
function PublicFormContent({
    configuration: config,
    available = true,
    preview = false,
    onSubmit,
    pending,
    receipt,
    error,
    onReload,
    turnstileSiteKey,
    resolvedAppearance = 'light',
}: PublicFormPageProps) {
    const content = useEmbeddedHeight(preview)
    const { token } = theme.useToken()
    const { t: translate } = useTranslation()
    const t = (key: string) => translate(key, { lng: config.locale })
    const [turnstileToken, setTurnstileToken] = useState<string>()
    const [attempt, setAttempt] = useState(0)
    const protectedForm = !preview
    const [simulated, setSimulated] = useState(false)
    const [form] = Form.useForm()
    const style = {
        background: token.colorBgLayout,
        color: token.colorText,
        fontFamily: token.fontFamily,
        '--application-focus': token.colorPrimary,
        '--application-checkbox-offset': `${Math.max(0, (token.fontSize * token.lineHeight - token.controlInteractiveSize) / 2)}px`,
    } as CSSProperties
    return (
        <main
            className="public-application min-h-screen px-5 py-10"
            style={style}
        >
            <div
                ref={content}
                className="mx-auto max-w-[480px]"
            >
                <Flex
                    vertical
                    gap={24}
                    className="mb-6"
                >
                    <Flex
                        vertical
                        gap={12}
                        align="start"
                    >
                        {config.logoUrl && (
                            <img
                                className="max-h-16 max-w-[180px] object-contain"
                                src={assetUrl(config.logoUrl)}
                                alt={config.brandName}
                            />
                        )}
                        <Typography.Text strong>{config.brandName}</Typography.Text>
                    </Flex>
                    <Flex
                        vertical
                        gap={12}
                    >
                        <Typography.Title
                            level={2}
                            className="m-0"
                        >
                            {config.heading}
                        </Typography.Title>
                        {config.introduction && (
                            <Typography.Paragraph className="m-0 whitespace-pre-wrap">
                                {config.introduction}
                            </Typography.Paragraph>
                        )}
                    </Flex>
                </Flex>
                <Card>
                    {!available ? (
                        <Result
                            title={t('Applications are closed')}
                            subTitle={t('This campaign is not accepting applications right now.')}
                        />
                    ) : receipt || simulated ? (
                        <>
                            <Result
                                status={receipt?.state === 'attention' ? 'warning' : 'success'}
                                title={t(preview ? 'Preview complete' : 'Application received')}
                                subTitle={t(
                                    preview
                                        ? 'This is a preview. No candidate or WhatsApp message was created.'
                                        : 'You’ll receive a WhatsApp message from us shortly. You don’t need to do anything now.'
                                )}
                            />
                            {preview && (
                                <Button
                                    block
                                    onClick={() => {
                                        setSimulated(false)
                                        form.resetFields()
                                    }}
                                >
                                    {t('Restart preview')}
                                </Button>
                            )}
                        </>
                    ) : (
                        <Form
                            component={preview ? 'div' : 'form'}
                            form={form}
                            layout="vertical"
                            requiredMark
                            onFinish={async (values) => {
                                if (preview) {
                                    setSimulated(true)
                                    return
                                }
                                if (protectedForm && !turnstileToken) return
                                try {
                                    await onSubmit?.({
                                        ...values,
                                        phone: parseMobileNumber(values.phone)!.number,
                                        ...(protectedForm ? { turnstileToken } : {}),
                                    })
                                } finally {
                                    if (protectedForm) {
                                        setTurnstileToken(undefined)
                                        setAttempt((value) => value + 1)
                                    }
                                }
                            }}
                        >
                            {error && (
                                <Alert
                                    className="mb-5"
                                    type="error"
                                    showIcon
                                    message={error}
                                    action={onReload && <Button onClick={onReload}>{t('Reload form')}</Button>}
                                />
                            )}
                            <Form.Item
                                label={t('First name')}
                                name="firstName"
                                rules={[{ required: true, whitespace: true, message: t('Enter your first name') }]}
                            >
                                <Input
                                    autoComplete="given-name"
                                    maxLength={100}
                                />
                            </Form.Item>
                            <Form.Item
                                label={t('Last name')}
                                name="lastName"
                                rules={[{ required: true, whitespace: true, message: t('Enter your last name') }]}
                            >
                                <Input
                                    autoComplete="family-name"
                                    maxLength={100}
                                />
                            </Form.Item>
                            <Form.Item
                                label={t('Mobile number')}
                                validateFirst
                                name="phone"
                                extra={t('We’ll start your WhatsApp conversation on this number.')}
                                rules={[
                                    { required: true, message: t('Enter your mobile number') },
                                    {
                                        validator: (_, value) =>
                                            !value?.number?.trim()
                                                ? Promise.reject(new Error(t('Enter your mobile number')))
                                                : parseMobileNumber(value)?.isPossible()
                                                  ? Promise.resolve()
                                                  : Promise.reject(new Error(t('Enter a valid mobile number'))),
                                    },
                                ]}
                            >
                                <MobileNumberInput
                                    locale={config.locale}
                                    countryLabel={t('Country code')}
                                    numberLabel={t('Mobile number')}
                                />
                            </Form.Item>
                            <Form.Item
                                label={t('Email (optional)')}
                                name="email"
                                rules={[{ type: 'email', message: t('Enter a valid email address') }]}
                            >
                                <Input
                                    type="email"
                                    autoComplete="email"
                                    maxLength={254}
                                />
                            </Form.Item>
                            <Form.Item
                                name="website"
                                className="application-honeypot"
                            >
                                <Input
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                />
                            </Form.Item>
                            <Form.Item
                                name="consent"
                                valuePropName="checked"
                                rules={[
                                    {
                                        validator: (_, value) =>
                                            value
                                                ? Promise.resolve()
                                                : Promise.reject(new Error(t('Please agree before continuing'))),
                                    },
                                ]}
                            >
                                <Checkbox>{t('I agree to be contacted on WhatsApp about this application.')}</Checkbox>
                            </Form.Item>
                            {protectedForm && turnstileSiteKey && (
                                <TurnstileCheck
                                    siteKey={turnstileSiteKey}
                                    showCheckingStatus={false}
                                    appearance={resolvedAppearance}
                                    locale={config.locale}
                                    resetKey={attempt}
                                    onToken={setTurnstileToken}
                                />
                            )}
                            {protectedForm && !turnstileSiteKey && (
                                <Alert
                                    className="mb-4"
                                    type="error"
                                    message={t('Verification failed. Please retry the security check.')}
                                />
                            )}
                            <Button
                                type="primary"
                                htmlType={preview ? 'button' : 'submit'}
                                onClick={preview ? () => form.submit() : undefined}
                                block
                                loading={pending}
                                disabled={protectedForm && !turnstileToken}
                            >
                                {protectedForm && turnstileSiteKey && !turnstileToken && !pending
                                    ? t('Checking…')
                                    : config.buttonLabel}
                            </Button>
                            <Typography.Paragraph
                                type="secondary"
                                className="mt-4 mb-0"
                            >
                                {t('Your details are used for your application.')}{' '}
                                <Typography.Link
                                    href={PRIVACY_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('Privacy notice')}
                                </Typography.Link>
                            </Typography.Paragraph>
                        </Form>
                    )}
                </Card>
                <Flex
                    justify="center"
                    className="mt-5"
                >
                    <Typography.Text
                        type="secondary"
                        className="text-xs"
                    >
                        {t('Powered by')}{' '}
                        <Typography.Link
                            href={`https://www.evaa.chat/?utm_source=public_form&utm_medium=referral&utm_campaign=${receipt || simulated ? 'confirmation' : 'application'}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs !text-inherit underline decoration-current/30 underline-offset-2"
                        >
                            {EVAA_NAME}
                        </Typography.Link>
                    </Typography.Text>
                </Flex>
            </div>
        </main>
    )
}

/**
 * In the apply modal on a customer's website (an iframe), tells the modal how tall the form is, so it can fit the form
 * without scrolling when the window has room. Only a height is sent, so any parent may receive it.
 */
function useEmbeddedHeight(preview: boolean) {
    const content = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const element = content.current

        if (preview || !element || window.parent === window) {
            return
        }

        const report = () => {
            const main = element.parentElement
            const padding = main
                ? parseFloat(getComputedStyle(main).paddingTop) + parseFloat(getComputedStyle(main).paddingBottom)
                : 0

            window.parent.postMessage(
                { type: 'evaa-apply-height', height: Math.ceil(element.offsetHeight + padding) },
                '*'
            )
        }
        const observer = new ResizeObserver(report)

        observer.observe(element)
        report()

        return () => observer.disconnect()
    }, [preview])

    return content
}
